import React from "react"
import * as Yup from "yup"
import {
  Box, Text, VStack, List, ListItem,
} from "@chakra-ui/react"
import {
  DragDropContext, Droppable, Draggable, DropResult,
} from "react-beautiful-dnd"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import TextField from "components/ReactHookForm/TextField"
import SwitchField from "components/ReactHookForm/SwitchField"
import { Website, Speaker } from "sharedTypes"
import * as api from "api/websites"
import { updateSpeakers, updateWebsite } from "context/actions"
import { useQueryClient } from "react-query"
import { moveElement, sortByOrderListWithPosition } from "utilities/objects"
import Icon from "components/elements/Icon"
import { SpeakerSettingsFormValues } from "pages/EventShow/WebPage/types"
import SpeakerDisplay from "components/elements/SpeakerDisplay"

interface Props {
  eventId: string
  website: Website
  onClose: () => void
  onHide: () => void
  onChange?: (values: SpeakerSettingsFormValues) => void
}

const SpeakerSettings = ({
  eventId, website, onClose, onHide, onChange,
}: Props) => {
  const queryClient = useQueryClient()
  const initialOrder = website.speakers.map((speaker) => speaker.id)

  const onSubmit = (values: SpeakerSettingsFormValues): void => {
    const { speakerOrder, ...websiteValues } = values
    const submitValues = {
      speakersEnabled: websiteValues.speakersEnabled,
      speakersHeader: websiteValues.speakersHeader,
    }

    const saveWebsiteThenHide = () => api.putWebsite(eventId, submitValues)
      .then(({ data }) => {
        updateWebsite(queryClient, eventId, data)
        onHide()
      })

    if (speakerOrder && speakerOrder.length > 0) {
      const sortedSpeakers = sortByOrderListWithPosition<Speaker>(website.speakers, speakerOrder)

      api.postSpeakersOrder(eventId, speakerOrder)
        .then(() => {
          updateSpeakers(queryClient, eventId, sortedSpeakers)

          return saveWebsiteThenHide()
        })
    } else {
      saveWebsiteThenHide()
    }
  }

  const validationSchema = Yup.object().shape({
    speakersEnabled: Yup.boolean(),
    speakersHeader: Yup.string(),
    speakerOrder: Yup.array().of(Yup.number()),
  })

  return (
    <ModalForm
      title="Speaker Settings"
      size="lg"
      onClose={onClose}
      initialValues={{
        speakersEnabled: website.speakersEnabled ?? true,
        speakersHeader: website.speakersHeader || "Speakers",
        speakerOrder: initialOrder,
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      disableSubmitWhenNotDirty
      onChange={onChange}
    >
      {({ control, watch, setValue }) => {
        const speakerOrder = watch("speakerOrder") || initialOrder
        const sortedSpeakers = sortByOrderListWithPosition<Speaker>(website.speakers, speakerOrder)

        const onDragEnd = (result: DropResult) => {
          const { source, destination } = result

          if (!destination) {
            return
          }

          const newOrder = moveElement(speakerOrder, source.index, destination.index)
          setValue("speakerOrder", newOrder, { shouldDirty: true })

          // Update preview with reordered speakers
          if (onChange) {
            const reorderedSpeakers = sortByOrderListWithPosition<Speaker>(
              website.speakers,
              newOrder,
            )
            onChange({ speakers: reorderedSpeakers })
          }
        }

        return (
          <VStack spacing={6} align="stretch">
            <SwitchField
              name="speakersEnabled"
              control={control}
              label="Show Speakers"
              fontSize="2xl"
              fontWeight="normal"
            />

            <Box mt={-2}>
              <TextField
                name="speakersHeader"
                control={control}
                label="Header"
                variant="outline"
                vertical
                mb={0}
              />
            </Box>

            {sortedSpeakers.length > 0 && (
              <Box mt={4}>
                <Text fontSize="md" fontWeight="semibold" mb={2}>
                  Speaker Order
                </Text>
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="speakers-order-droppable">
                    {(provided) => (
                      <List
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        spacing={3}
                      >
                        {sortedSpeakers.map((speaker, index) => (
                          <Draggable
                            key={speaker.id}
                            draggableId={speaker.id.toString()}
                            index={index}
                          >
                            {(providedInner) => (
                              <ListItem
                                ref={providedInner.innerRef}
                                {...providedInner.draggableProps}
                                {...providedInner.dragHandleProps}
                                display="flex"
                                alignItems="center"
                                p={3}
                                borderWidth={1}
                                borderRadius="md"
                                borderColor="gray.200"
                                bgColor="white"
                                mb={2}
                              >
                                <Box flex={1} minW={0}>
                                  <SpeakerDisplay {...speaker} />
                                </Box>
                                <Icon icon="menu" color="gray.400" size={6} />
                              </ListItem>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </List>
                    )}
                  </Droppable>
                </DragDropContext>
              </Box>
            )}
          </VStack>
        )
      }}
    </ModalForm>
  )
}

export default SpeakerSettings
